import { Autocomplete, Box, Grid, MenuItem, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CustomerIntakeForm } from '../types/CustomerIntakeForm';

interface OnboardingCommercialComplianceProps {
  formData: CustomerIntakeForm;
  handleChange: (field: keyof CustomerIntakeForm, value: string) => void;
  handleTagsChange: (field: keyof CustomerIntakeForm, value: string[]) => void;
}

const PRICING_MODELS = ['Management fee + pass-through cost', 'Fixed price per cover', 'Cost plus', 'Concession (revenue share)'];
const COMPLIANCE_OPTIONS = ['Allergen labelling (Natasha\'s Law)', 'Food Standards Agency hygiene rating', 'HACCP', 'Nutritional standards (school food/NHS)', 'Modern Slavery Act', 'Sustainability/net zero commitments'];
const REPORTING_OPTIONS = ['Executive dashboard', 'Financial reporting', 'Menu compliance', 'Allergen compliance', 'Waste & sustainability', 'Client satisfaction/NPS'];

const OnboardingCommercialCompliance: React.FC<OnboardingCommercialComplianceProps> = ({ formData, handleChange, handleTagsChange }) => {
  const { t } = useTranslation('common');
  const textFieldMargin = { mb: 8 };

  return (
    <Box sx={{ p: 6, paddingBottom: 0 }}>
      <Grid container
        spacing={3}>
        <Grid item
          xs={12}
          sx={{ pr: 4 }}
          md={6}>
          <TextField
            fullWidth
            required
            label={t('onboardingIntake.budget', 'Budget')}
            placeholder={t('onboardingIntake.budgetPlaceholder', 'e.g. £2.4m / year')}
            value={formData.budget}
            onChange={(e) => handleChange('budget', e.target.value)}
            sx={textFieldMargin}
          />
          <TextField select
            fullWidth
            required
            label={t('onboardingIntake.pricingModel', 'Pricing model')}
            value={formData.pricingModel}
            onChange={(e) => handleChange('pricingModel', e.target.value)}
            sx={textFieldMargin}>
            {PRICING_MODELS.map((option) => (
              <MenuItem key={option}
                value={option}>{option}</MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item
          sx={{ pl: 4 }}
          xs={12}
          md={6}>
          <Autocomplete
            multiple
            freeSolo
            options={COMPLIANCE_OPTIONS}
            value={formData.complianceExpectations}
            onChange={(_, newValue) => handleTagsChange('complianceExpectations', newValue as string[])}
            renderInput={(params) => (
              <TextField {...params}
                label={t('onboardingIntake.complianceExpectations', 'Compliance expectations')}
                required={formData.complianceExpectations.length === 0} />
            )}
            sx={textFieldMargin}
          />
          <Autocomplete
            multiple
            freeSolo
            options={REPORTING_OPTIONS}
            value={formData.reportingRequirements}
            onChange={(_, newValue) => handleTagsChange('reportingRequirements', newValue as string[])}
            renderInput={(params) => (
              <TextField {...params}
                label={t('onboardingIntake.reportingRequirements', 'Reporting requirements')}
                required={formData.reportingRequirements.length === 0} />
            )}
            sx={textFieldMargin}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default OnboardingCommercialCompliance;
